import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { API_URL } from "../../utils/config";
import { formatNumero } from "../../utils/Utilitats";
import { mapToPresentable, mapejos } from "../../utils/Mapejos";

const PAGE_SIZES = [10, 25, 50];

function idProducte(p) {
  return p ? Number(p.idProductes) : null;
}

function idTipusDe(item) {
  return item ? Number(item.idTipusDeMaterials ?? item.TipusDeMaterials_idTipusDeMaterials) : null;
}

function idCategoriaDe(item) {
  return item ? Number(item.idCategories ?? item.Categories_idCategories) : null;
}

function esPack(p) {
  return Number(p?.EsPack) === 1;
}

function Seleccionarproducte({
  onProductSelect,
  onConsultar,
  onModificar,
  onEsborrar,
  onPreus,
  onAtributs,
  onPack,
  selectedProduct,
  setProductes,
  filterText,
  setFilterText,
  categoriaFilter,
  setCategoriaFilter,
  tipusFilter,
  setTipusFilter,
  materialFilter,
  setMaterialFilter,
  reloadToken,
}) {
  const { empresaSeleccionada, rolUsuari } = useUserContext();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tipusList, setTipusList] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "Marca", direction: "asc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [menuObert, setMenuObert] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const menuRef = useRef(null);

  const pucConsultar = tincPermis(rolUsuari, "CONSULTAR");
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const pucEsborrar = tincPermis(rolUsuari, "ESBORRAR");

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
  };

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      if (!empresaSeleccionada?.IdEmpresa) return;
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/Productes.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idEmpresa: empresaSeleccionada.IdEmpresa }),
        });
        const result = await response.json();
        setProducts(result.success ? result.productes || [] : []);
      } catch (err) {
        console.error("Error carregant productes:", err);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [empresaSeleccionada, reloadToken]);

  useEffect(() => {
    const fetchCategories = async () => {
      if (!empresaSeleccionada?.IdEmpresa) return;
      try {
        const res = await fetch(`${API_URL}/Categories.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idEmpresa: empresaSeleccionada.IdEmpresa }),
        });
        const data = await res.json();
        setCategories(data.success ? data.categories || [] : []);
      } catch (err) {
        setCategories([]);
      }
    };
    fetchCategories();
  }, [empresaSeleccionada]);

  useEffect(() => {
    const fetchTipus = async () => {
      if (!empresaSeleccionada?.IdEmpresa) return;
      try {
        const res = await fetch(`${API_URL}/Tipusdematerials.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idEmpresa: empresaSeleccionada.IdEmpresa,
            mostrarActius: 1,
          }),
        });
        const data = await res.json();
        setTipusList(data.success ? data.tipusDeMaterials || [] : []);
      } catch (err) {
        setTipusList([]);
      }
    };
    fetchTipus();
  }, [empresaSeleccionada]);

  useEffect(() => {
    const fetchMaterials = async () => {
      if (!empresaSeleccionada?.IdEmpresa) return;
      try {
        const res = await fetch(`${API_URL}/Materials.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idEmpresa: empresaSeleccionada.IdEmpresa,
            mostrarActius: 1,
          }),
        });
        const data = await res.json();
        setMaterials(data.success ? data.materials || [] : []);
      } catch (err) {
        setMaterials([]);
      }
    };
    fetchMaterials();
  }, [empresaSeleccionada]);

  useEffect(() => {
    const lower = (filterText || "").toLowerCase();
    const filtered = (products || []).filter((p) => {
      if (categoriaFilter) {
        const catId = idCategoriaDe(p);
        if (catId) {
          if (catId !== Number(categoriaFilter)) return false;
        } else {
          const cat = categories.find((c) => Number(c.idCategories) === Number(categoriaFilter));
          if (cat && p.Categoria !== cat.Categoria) return false;
        }
      }
      if (tipusFilter) {
        const tId = idTipusDe(p);
        if (tId) {
          if (tId !== Number(tipusFilter)) return false;
        } else {
          const t = tipusList.find((x) => idTipusDe(x) === Number(tipusFilter));
          if (t && p.Tipus !== t.Tipus) return false;
        }
      }
      if (materialFilter && Number(p.Materials_idMaterials ?? p.idMaterials) !== Number(materialFilter)) {
        return false;
      }
      const camp = [p.Tipus, p.NomGeneric, p.Marca, p.Model, p.Qualitat, p.Descripcio, p.Categoria, p.RefProducte]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const packStr = esPack(p) ? "sí pack" : "no";
      return !lower || camp.includes(lower) || packStr.includes(lower);
    });

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        if (sortConfig.key === "EsPack") {
          const aValue = esPack(a) ? 1 : 0;
          const bValue = esPack(b) ? 1 : 0;
          if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
          if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
          return 0;
        }
        if (sortConfig.key === "EstocFisic" || sortConfig.key === "EstocReservat" || sortConfig.key === "EstocDisponible") {
          const valor = (fila) => {
            if (sortConfig.key === "EstocReservat") return Number(fila.EstocReservat) || 0;
            if (sortConfig.key === "EstocDisponible") return (Number(fila.EstocFisic) || 0) - (Number(fila.EstocReservat) || 0);
            return Number(fila.EstocFisic) || 0;
          };
          const aValue = valor(a);
          const bValue = valor(b);
          if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
          if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
          return 0;
        }
        const aValue = (a[sortConfig.key] || "").toString().toLowerCase();
        const bValue = (b[sortConfig.key] || "").toString().toLowerCase();
        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    setFilteredProducts(filtered);
    setProductes?.(filtered);
    setPage(1);
  }, [filterText, categoriaFilter, tipusFilter, materialFilter, products, sortConfig, setProductes, categories, tipusList]);

  useEffect(() => {
    if (!selectedProduct) return;
    const stillVisible = filteredProducts.some((p) => idProducte(p) === idProducte(selectedProduct));
    if (!stillVisible) onProductSelect?.(null);
  }, [filteredProducts, selectedProduct, onProductSelect]);

  useEffect(() => {
    const tancar = (e) => {
      if (e.target.closest(".dv2-menu") || e.target.closest(".dv2-menuBtn")) return;
      setMenuObert(null);
    };
    document.addEventListener("mousedown", tancar);
    return () => document.removeEventListener("mousedown", tancar);
  }, []);

  const obrirMenu = (e, product) => {
    e.stopPropagation();
    const id = idProducte(product);
    if (menuObert === id) {
      setMenuObert(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const ample = 220;
    const alt = esPack(product) ? 250 : 220;
    const marge = 8;
    let top = rect.bottom + 4;
    if (top + alt > window.innerHeight - marge) {
      top = Math.max(marge, rect.top - alt - 4);
    }
    let left = rect.right - ample;
    if (left < marge) left = marge;
    if (left + ample > window.innerWidth - marge) {
      left = window.innerWidth - ample - marge;
    }
    setMenuPos({ top, left });
    setMenuObert(id);
  };

  const total = filteredProducts.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const paginaActual = Math.min(page, totalPages);
  const inici = (paginaActual - 1) * pageSize;
  const paginaItems = filteredProducts.slice(inici, inici + pageSize);
  const productMenu = menuObert ? filteredProducts.find((p) => idProducte(p) === menuObert) : null;
  const tipusFiltrats = (tipusList || []).filter(
    (t) => !categoriaFilter || idCategoriaDe(t) === Number(categoriaFilter) || t.Categoria === categoriaFilter
  );
  const materialsFiltrats = (materials || []).filter((m) => {
    if (categoriaFilter && idCategoriaDe(m) !== Number(categoriaFilter) && m.Categoria !== categoriaFilter) return false;
    if (tipusFilter && idTipusDe(m) !== Number(tipusFilter) && m.Tipus !== tipusFilter) return false;
    return true;
  });

  const paginesVisibles = () => {
    const pages = [];
    const max = totalPages;
    const actual = paginaActual;
    const afegir = (n) => {
      if (!pages.includes(n)) pages.push(n);
    };
    afegir(1);
    for (let i = actual - 1; i <= actual + 1; i += 1) {
      if (i > 1 && i < max) afegir(i);
    }
    if (max > 1) afegir(max);
    return pages.sort((a, b) => a - b);
  };

  const renderAccions = (product) => (
    <div className="dv2-table-actions">
      <button
        type="button"
        className={`dv2-menuBtn ${menuObert === idProducte(product) ? "is-open" : ""}`}
        aria-label="Accions"
        onClick={(e) => obrirMenu(e, product)}
      >
        …
      </button>
    </div>
  );

  const th = (key, label, extraClass = "") => (
    <th className={`${extraClass} ${sortConfig.key === key ? "is-sorted" : ""}`} onClick={() => handleSort(key)}>
      {label} {sortConfig.key === key ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
    </th>
  );

  return (
    <div className="dv2-listBody">
      <div className="dv2-filterBar">
        <div className="dv2-filter dv2-filter--grow">
          <span className="dv2-filter-label">Cerca</span>
          <input
            type="text"
            className="dv2-input dv2-input-search"
            placeholder="Cerca per marca, model, material…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>
        <div className="dv2-filter">
          <span className="dv2-filter-label">Categoria</span>
          <select
            className="dv2-select"
            value={categoriaFilter}
            onChange={(e) => {
              setCategoriaFilter(e.target.value);
              setTipusFilter("");
              setMaterialFilter("");
            }}
          >
            <option value="">Totes</option>
            {categories.map((cat) => (
              <option key={cat.idCategories} value={String(cat.idCategories)}>
                {cat.Categoria}
              </option>
            ))}
          </select>
        </div>
        <div className="dv2-filter">
          <span className="dv2-filter-label">Tipus</span>
          <select
            className="dv2-select"
            value={tipusFilter}
            onChange={(e) => {
              setTipusFilter(e.target.value);
              setMaterialFilter("");
            }}
          >
            <option value="">Tots</option>
            {tipusFiltrats.map((t) => (
              <option key={idTipusDe(t)} value={String(idTipusDe(t))}>
                {t.Tipus}
              </option>
            ))}
          </select>
        </div>
        <div className="dv2-filter">
          <span className="dv2-filter-label">Material</span>
          <select
            className="dv2-select"
            value={materialFilter || ""}
            onChange={(e) => setMaterialFilter(e.target.value)}
          >
            <option value="">Tots</option>
            {materialsFiltrats.map((m) => (
              <option key={m.idMaterials} value={String(m.idMaterials)}>
                {m.NomGeneric}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <p className="dv2-empty">Carregant productes…</p>
      ) : filteredProducts.length === 0 ? (
        <p className="dv2-empty">No s'ha trobat cap producte.</p>
      ) : (
        <>
          {isNarrow ? (
            <div className="dv2-recordList" style={{ display: "flex" }}>
              {paginaItems.map((product) => (
                <article
                  key={idProducte(product)}
                  className="dv2-record"
                  onClick={() => onProductSelect?.(product)}
                  onDoubleClick={() => pucConsultar && onConsultar?.(product)}
                >
                  <div>
                    <p className="dv2-record-title">
                      {[product.Marca, product.Model].filter(Boolean).join(" ") || "—"}
                    </p>
                    <p className="dv2-record-meta">
                      {[product.NomGeneric || product.Categoria, esPack(product) ? "Pack" : null]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    <p className="dv2-record-meta">
                      Estoc {formatNumero(product.EstocFisic)} · Reservat {formatNumero(product.EstocReservat)} · Disp.{" "}
                      {formatNumero((Number(product.EstocFisic) || 0) - (Number(product.EstocReservat) || 0))}
                    </p>
                  </div>
                  {renderAccions(product)}
                </article>
              ))}
            </div>
          ) : (
            <div className="dv2-tableWrap">
              <table className="dv2-table">
                <thead>
                  <tr>
                    {th("Marca", "Marca")}
                    {th("Model", "Model")}
                    {th("Descripcio", "Descripció")}
                    {th("Qualitat", "Qualitat")}
                    {th("EsPack", "Pack")}
                    {th("EstocFisic", "Estoc físic", "dv2-num")}
                    {th("EstocReservat", "Reservat", "dv2-num")}
                    {th("EstocDisponible", "Disponible", "dv2-num")}
                    <th className="dv2-table-actions">Accions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginaItems.map((product) => (
                    <tr
                      key={idProducte(product)}
                      className={idProducte(selectedProduct) === idProducte(product) ? "is-selected" : ""}
                      onClick={() => onProductSelect?.(product)}
                      onDoubleClick={() => pucConsultar && onConsultar?.(product)}
                    >
                      <td>{product.Marca || "—"}</td>
                      <td>{product.Model || "—"}</td>
                      <td>{product.Descripcio || "—"}</td>
                      <td>{mapToPresentable(product.Qualitat, mapejos.qualitat) || product.Qualitat || "—"}</td>
                      <td>{esPack(product) ? "Sí" : "No"}</td>
                      <td className="dv2-num">{formatNumero(product.EstocFisic)}</td>
                      <td className="dv2-num">{formatNumero(product.EstocReservat)}</td>
                      <td className="dv2-num">
                        {formatNumero((Number(product.EstocFisic) || 0) - (Number(product.EstocReservat) || 0))}
                      </td>
                      <td className="dv2-table-actions">{renderAccions(product)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="dv2-pagination">
            <span>
              Mostrant {inici + 1} a {Math.min(inici + pageSize, total)} de {total} resultats
            </span>
            <div className="dv2-pagination-pages">
              <button type="button" className="dv2-pageBtn dv2-pageBtn--nav" disabled={paginaActual <= 1} onClick={() => setPage(1)}>
                «
              </button>
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ‹
              </button>
              {paginesVisibles().map((n, idx, arr) => (
                <React.Fragment key={n}>
                  {idx > 0 && n - arr[idx - 1] > 1 && <span>…</span>}
                  <button
                    type="button"
                    className={`dv2-pageBtn ${n === paginaActual ? "is-active" : ""}`}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                </React.Fragment>
              ))}
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                ›
              </button>
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual >= totalPages}
                onClick={() => setPage(totalPages)}
              >
                »
              </button>
            </div>
            <label className="dv2-filter">
              <span className="dv2-filter-label">Files per pàgina</span>
              <select
                className="dv2-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </>
      )}
      {productMenu &&
        menuPos &&
        createPortal(
          <div className="dv2-menu dv2-menu--fixed" ref={menuRef} style={{ top: menuPos.top, left: menuPos.left }}>
            {pucConsultar && (
              <button type="button" className="dv2-menu-item" onClick={() => { setMenuObert(null); onConsultar?.(productMenu); }}>
                Consultar
              </button>
            )}
            {pucModificar && (
              <button type="button" className="dv2-menu-item" onClick={() => { setMenuObert(null); onModificar?.(productMenu); }}>
                Modificar
              </button>
            )}
            {pucModificar && (
              <button type="button" className="dv2-menu-item" onClick={() => { setMenuObert(null); onPreus?.(productMenu); }}>
                Preus proveïdors
              </button>
            )}
            {pucModificar && (
              <button type="button" className="dv2-menu-item" onClick={() => { setMenuObert(null); onAtributs?.(productMenu); }}>
                Atributs
              </button>
            )}
            {pucModificar && esPack(productMenu) && (
              <button type="button" className="dv2-menu-item" onClick={() => { setMenuObert(null); onPack?.(productMenu); }}>
                Pack
              </button>
            )}
            {pucEsborrar && (
              <button type="button" className="dv2-menu-item is-danger" onClick={() => { setMenuObert(null); onEsborrar?.(productMenu); }}>
                Esborrar
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export default Seleccionarproducte;
